import {
  Box,
  Divider,
  Radio,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { type TPaymentOption, useSubmission } from 'api/submission';
import { type FC, Fragment } from 'react';
import { useParams } from 'react-router-dom';
import { QuoteDetailsCardNoteStyle } from '../../QuoteDetailsCard.styles';
import {
  PaymentOptionSelectButtonStyle,
  PaymentOptionSelectDetailsListStyle,
  PaymentOptionSelectDetailStyle,
  PaymentOptionSelectHeaderStyle,
  PaymentOptionSelectNoteStyle,
  PaymentOptionSelectStyle,
  PaymentOptionSelectTitleStyle,
  PaymentOptionSelectTitleValueStyle,
  PaymentOptionSelectToggleStyle,
} from './PaymentOptionSelect.styles';
import type { PaymentOptionSelectProps } from './PaymentOptionSelect.types';

const PaymentOptionSelect: FC<PaymentOptionSelectProps> = ({
  monthlyPayment,
  totalPayment,
  discountedTotalDue,
  setPaymentOption,
  paymentOption,
  fullPaymentSavings,
  priceLoading = true,
  isViewOnly = false,
}) => {
  const { id } = useParams() as { id: string };
  const { data: submission } = useSubmission(id);

  const handleSelectOption = (_e: unknown, value: TPaymentOption) => {
    if (value != null && monthlyPayment && totalPayment) setPaymentOption(value);
  };

  const paymentOptionsData = [
    {
      option: 'Monthly',
      title: 'Pay Monthly',
      value: monthlyPayment,
      details: [
        {
          desc: 'Due today',
          value: monthlyPayment,
        },
        {
          desc: 'Then 11 monthly payments',
          value: monthlyPayment,
        },
        {
          desc: 'Over 12 months',
          value: totalPayment,
        },
      ],
    },
    {
      option: 'Annual',
      title: 'Pay in full for 12 months',
      value: discountedTotalDue,
      note:
        submission?.us_state !== 'CA'
          ? `Save ${fullPaymentSavings} over 12 months by paying in full today.`
          : '',
    },
  ];

  return (
    <Stack sx={PaymentOptionSelectStyle}>
      <Typography textAlign="center">Select your payment plan</Typography>

      <ToggleButtonGroup
        disabled={isViewOnly}
        exclusive
        value={paymentOption}
        onChange={handleSelectOption}
        sx={PaymentOptionSelectToggleStyle}
      >
        {paymentOptionsData.map((option) => (
          <ToggleButton
            key={option.option}
            value={option.option}
            sx={PaymentOptionSelectButtonStyle}
            data-test={option.option}
          >
            <Stack sx={PaymentOptionSelectHeaderStyle}>
              <Radio
                checked={paymentOption === option.option}
                disableRipple
                disableFocusRipple
                disableTouchRipple
              />

              <Box sx={PaymentOptionSelectTitleStyle}>
                <Typography marginBlock={0.75} component="p" variant="h3">
                  {option.title}
                </Typography>

                {totalPayment && monthlyPayment && !priceLoading ? (
                  <Typography sx={PaymentOptionSelectTitleValueStyle} component="p" variant="h1">
                    {option.value}
                  </Typography>
                ) : (
                  <Skeleton width="4ch" height="3.5rem" sx={{ display: 'inline-block' }} />
                )}
              </Box>
            </Stack>

            {option.details && (
              <Stack sx={PaymentOptionSelectDetailsListStyle}>
                {option.details.map((detail, idx, arr) => (
                  <Fragment key={detail.desc}>
                    {idx === arr.length - 1 && <Divider />}

                    {totalPayment && monthlyPayment && !priceLoading ? (
                      <Box sx={PaymentOptionSelectDetailStyle}>
                        <Typography component="span">{detail.desc}:</Typography>

                        <Typography component="span" variant="h3">
                          {detail.value}
                        </Typography>
                      </Box>
                    ) : (
                      <Skeleton height={20} />
                    )}
                  </Fragment>
                ))}
              </Stack>
            )}

            {option.note ? (
              !priceLoading ? (
                <Typography sx={PaymentOptionSelectNoteStyle}>{option.note}</Typography>
              ) : (
                <Skeleton width="12rem" height="3.5rem" sx={{ display: 'inline-block' }} />
              )
            ) : null}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Typography variant="h3" sx={QuoteDetailsCardNoteStyle}>
        Cancel anytime. Refunds are pro-rata.
      </Typography>
    </Stack>
  );
};

export default PaymentOptionSelect;
